import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  ClockIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { createCheckoutSession } from '../api/stripe';
import { UserSubscriptionStatus } from '../types/user.types';

interface TrialExpiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  daysRemaining?: number;
  subscriptionStatus?: UserSubscriptionStatus;
}

const TrialExpiryModal: React.FC<TrialExpiryModalProps> = ({
  isOpen,
  onClose,
  daysRemaining = 0,
  subscriptionStatus,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Lock body scroll when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleUpgrade = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const priceId = import.meta.env.VITE_STRIPE_PRICE_ID;
      if (!priceId) {
        throw new Error('Price ID not configured');
      }

      const session = await createCheckoutSession(priceId);
      if (session.url) {
        window.location.href = session.url;
      }
    } catch (err: any) {
      console.error('Error creating checkout session:', err);
      setError(err.message || 'Failed to start checkout process');
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const isExpired =
    subscriptionStatus === UserSubscriptionStatus.TRIAL_EXPIRED ||
    daysRemaining <= 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative transform overflow-hidden rounded-3xl bg-grey shadow-2xl transition-all w-full max-w-lg">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-white text-opacity-60 hover:text-opacity-100 transition-opacity"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          {/* Content */}
          <div className="p-8">
            {/* Icon */}
            <div
              className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${
                isExpired ? 'bg-red bg-opacity-20' : 'bg-orange bg-opacity-20'
              } mb-6`}
            >
              <ClockIcon
                className={`h-8 w-8 ${isExpired ? 'text-red' : 'text-orange'}`}
              />
            </div>

            {/* Title */}
            <h3 className="text-center text-2xl font-bold text-white mb-4">
              {isExpired ? 'Trial Expired' : 'Trial Expiring Soon'}
            </h3>

            {/* Message */}
            <p className="text-center text-white text-opacity-80 mb-8">
              {isExpired
                ? 'Your free trial has ended. Upgrade now to continue accessing all features.'
                : `Your trial expires in ${daysRemaining} ${
                    daysRemaining === 1 ? 'day' : 'days'
                  }. Upgrade now to ensure uninterrupted access.`}
            </p>

            {/* Error message */}
            {error && (
              <div className="bg-red bg-opacity-20 border border-red text-red rounded-lg p-3 mb-6 text-sm text-center">
                {error}
              </div>
            )}

            {/* Action buttons */}
            <div className="space-y-3">
              <button
                onClick={handleUpgrade}
                disabled={isLoading}
                className={`
                  w-full bg-violet hover:bg-violet-700 text-white font-medium py-3 px-6 rounded-full 
                  transition-all duration-200 flex items-center justify-center gap-2
                  ${
                    isLoading
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:shadow-lg'
                  }
                `}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <CreditCardIcon className="w-5 h-5" />
                    <span>Upgrade Now</span>
                  </>
                )}
              </button>

              {!isExpired && (
                <button
                  onClick={onClose}
                  className="w-full bg-white bg-opacity-10 hover:bg-opacity-20 text-white font-medium py-3 px-6 rounded-full transition-all duration-200"
                >
                  Remind Me Later
                </button>
              )}
            </div>

            <p className="text-center text-xs text-white text-opacity-60 mt-4">
              Cancel anytime. Secure payment via Stripe.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialExpiryModal;
